import React, { useState, useEffect, useRef } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Paper, 
  Grid,
  MenuItem,
  FormHelperText,
  Divider
} from '@mui/material';
import { useAuth, useNotification, VendorRegistrationData, useVendorQueries } from 'shared-lib';
import { InsuranceDocumentUploader } from './InsuranceDocumentUploader';

// List of US states for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

interface VendorRegistrationFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

// Типы для state формы и ошибок
interface FormErrors {
  companyName?: string;
  city?: string;
  state?: string;
  address?: string;
  zipCode?: string;
  contactFullName?: string;
  contactEmail?: string;
  contactPhoneNumber?: string;
  insuranceDocument?: string;
  [key: string]: string | undefined; // Index signature to allow string indexing
}

// Регулярные выражения для валидации
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/; // Формат: (123) 456-7890 или 123-456-7890
const ZIP_REGEX = /^\d{5}(-\d{4})?$/; // Формат: 12345 или 12345-6789

export const VendorRegistrationForm: React.FC<VendorRegistrationFormProps> = ({
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState<VendorRegistrationData>({
    companyName: '',
    city: '',
    state: '',
    address: '',
    zipCode: '',
    contactFullName: '',
    contactEmail: '',
    contactPhoneNumber: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  
  // Replace FirestoreService with React Query hooks
  const vendorQueries = useVendorQueries();
  const { useCreateVendor } = vendorQueries;
  const createVendor = useCreateVendor();

  // Валидация полей при изменении данных
  useEffect(() => {
    validateField();
  }, [formData]);

  const validateField = (field?: string) => {
    const newErrors: FormErrors = { ...errors };
    
    // Если передано конкретное поле, проверяем только его
    if (field) {
      validateSingleField(field, newErrors);
    } else {
      // Проверяем все поля
      Object.keys(formData).forEach(fieldName => {
        validateSingleField(fieldName, newErrors);
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateSingleField = (fieldName: string, errorsObj: FormErrors) => {
    // Changed: Validate insuranceDocument as required
    if (fieldName === 'insuranceDocument') {
      if (!formData.insuranceDocument) {
        errorsObj[fieldName] = 'Insurance document is required';
      } else {
        delete errorsObj[fieldName];
      }
      return;
    }

    const value = (formData as any)[fieldName];
    
    // Check if field is empty
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errorsObj[fieldName] = 'Field is required';
      return;
    }

    // Validate specific fields
    switch (fieldName) {
      case 'contactEmail':
        if (!EMAIL_REGEX.test(value)) {
          errorsObj[fieldName] = 'Please enter a valid email address';
        } else {
          delete errorsObj[fieldName];
        }
        break;
      
      case 'contactPhoneNumber':
        if (!PHONE_REGEX.test(value)) {
          errorsObj[fieldName] = 'Please enter a valid phone number (e.g., 123-456-7890)';
        } else {
          delete errorsObj[fieldName];
        }
        break;
      
      case 'zipCode':
        if (!ZIP_REGEX.test(value)) {
          errorsObj[fieldName] = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        } else {
          delete errorsObj[fieldName];
        }
        break;
      
      default:
        delete errorsObj[fieldName];
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to register as a vendor');
      return;
    }
    
    // Валидируем все поля перед отправкой и помечаем их как тронутые
    const touchedFields: Record<string, boolean> = {};
    Object.keys(formData).forEach(key => {
      touchedFields[key] = true;
    });
    setTouched(touchedFields);
    
    if (!validateField()) {
      setError('Please correct all errors before submitting.');
      return;
    }
    
    // Check if insurance document has been uploaded
    if (!formData.insuranceDocument) {
      setError('Please upload your insurance document before registering.');
      showNotification({
        message: 'Insurance document is required. Please upload it before continuing.',
        severity: 'warning'
      });
      return;
    }
    
    setError(null);
    
    try {
      // Use React Query mutation instead of FirestoreService
      await createVendor.mutateAsync(formData);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      if (onError) {
        onError(error);
      }
    }
  };

  // Use React Query's isPending state instead of local loading state
  const loading = createVendor.isPending;

  // Проверка, есть ли ошибка и было ли поле тронутым
  const hasError = (fieldName: keyof FormErrors) => 
    touched[fieldName] && Boolean(errors[fieldName]);
  
  // Получение текста ошибки
  const getErrorText = (fieldName: keyof FormErrors) => 
    touched[fieldName] ? errors[fieldName] : '';

  // Show error notification when there's an error
  const prevErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      prevErrorRef.current = error;
      showNotification({
        message: error,
        severity: 'error'
      });
    }
  }, [error, showNotification]);

  const handleInsuranceDocumentUpload = (documentData: {
    url: string;
    fileName: string;
    uploadedAt: Date;
  }) => {
    setFormData({
      ...formData,
      insuranceDocument: documentData
    });
  };

  const handleInsuranceDocumentRemove = () => {
    const newFormData = { ...formData };
    delete newFormData.insuranceDocument;
    setFormData(newFormData);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Register a Vendor Company
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="companyName"
              name="companyName"
              label="Company Name"
              value={formData.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('companyName')}
              helperText={getErrorText('companyName')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="address"
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('address')}
              helperText={getErrorText('address')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="city"
              name="city"
              label="City"
              value={formData.city}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('city')}
              helperText={getErrorText('city')}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              select
              id="state"
              name="state"
              label="State"
              value={formData.state}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('state')}
              helperText={getErrorText('state')}
            >
              {US_STATES.map(state => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              id="zipCode"
              name="zipCode"
              label="Zip Code"
              value={formData.zipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('zipCode')}
              helperText={getErrorText('zipCode')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="contactFullName"
              name="contactFullName"
              label="Contact Full Name"
              value={formData.contactFullName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactFullName')}
              helperText={getErrorText('contactFullName')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="contactEmail"
              name="contactEmail"
              label="Contact Email"
              type="email"
              value={formData.contactEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactEmail')}
              helperText={getErrorText('contactEmail')}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="contactPhoneNumber"
              name="contactPhoneNumber"
              label="Contact Phone Number"
              value={formData.contactPhoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              error={hasError('contactPhoneNumber')}
              helperText={getErrorText('contactPhoneNumber')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color={formData.insuranceDocument ? 'success.main' : 'text.secondary'}>
                Required Document 
                {formData.insuranceDocument 
                  ? ' (✓ Uploaded)' 
                  : ' - Must be uploaded before registration'}
              </Typography>
            </Box>
            <InsuranceDocumentUploader 
              onUploadComplete={handleInsuranceDocumentUpload}
              onRemove={handleInsuranceDocumentRemove}
              currentDocument={formData.insuranceDocument ? {
                url: formData.insuranceDocument.url,
                fileName: formData.insuranceDocument.fileName
              } : undefined}
            />
            {hasError('insuranceDocument') && (
              <FormHelperText error sx={{ mt: 1, ml: 2 }}>
                {getErrorText('insuranceDocument')}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? 'Registering...' : 'Register Vendor'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}; 